// Headline performance for the selected source + window, from the
// server-computed /api/journal/aggregates (exact full-set totals).

import type { JournalAggregates } from "../api/types";
import { usd, pct, day } from "../lib/format";

interface Props {
  aggregates: JournalAggregates | undefined;
  loading?: boolean;
}

export function HistoryStatsCard({ aggregates, loading }: Props) {
  if (loading && !aggregates) {
    return (
      <div className="card">
        <h2>Performance</h2>
        <div className="muted small">loading…</div>
      </div>
    );
  }
  if (!aggregates || aggregates.total_entries === 0) {
    return (
      <div className="card">
        <h2>Performance</h2>
        <div className="muted small">no trades recorded in this window yet — warming up</div>
      </div>
    );
  }

  const a = aggregates;
  const decided = a.total_wins + a.total_losses;
  const winRate = decided > 0 ? (100 * a.total_wins) / decided : 0;
  const closed = a.total_wins + a.total_losses + a.total_breakeven;
  const netCls = a.total_pnl_net > 0 ? "good" : a.total_pnl_net < 0 ? "bad" : "";

  return (
    <div className="card">
      <h2>
        Performance <span className="muted small">— {day(a.first_ts)} → {day(a.last_ts)}</span>
      </h2>
      <div className="stat-row">
        <div className="stat">
          <span className="stat-label">Net P&amp;L</span>
          <span className={"stat-value " + netCls}>{usd(a.total_pnl_net, { sign: true })}</span>
          <span className="small muted">{closed} closed</span>
        </div>
        <div className="stat">
          <span className="stat-label">Win rate</span>
          <span className="stat-value">{pct(winRate)}</span>
          <span className="small muted">W{a.total_wins} / L{a.total_losses} / BE{a.total_breakeven}</span>
        </div>
        <div className="stat">
          <span className="stat-label">Trades</span>
          <span className="stat-value">{a.total_entries}</span>
          <span className="small muted">{a.total_entries - closed} open</span>
        </div>
      </div>
      {a.by_source.length > 0 && (
        <div className="row wrap small" style={{ marginTop: 10 }}>
          {a.by_source.map((s) => (
            <span key={s.source} className="row" style={{ gap: 6, width: "auto" }}>
              <span className={"pill " + (s.source === "sim" ? "kind" : s.source === "live" ? "good" : "dim")}>
                {s.source}
              </span>
              <span className="mono">{usd(s.pnl_net, { sign: true })}</span>
              <span className="muted">({s.count})</span>
            </span>
          ))}
        </div>
      )}
    </div>
  );
}
